import { Point2D, Polygon, RobotSentence, RobotWordClick } from '../robots.model';
import { SentenceTypeEnum } from '../enums/sentence-type.enum';
import { ILandmark, ILandmarkFound } from './landmarks.interface';
import { WordDataTypeEnum } from '../enums/word-data-type.enum';
import { IClassificationParameters } from './classification-parameters.interface';
import { TPrimitiveType } from './files.interface';

/**
 * Representa o DTO de uma configuração de sentença para identificar um robô
 */
export interface IRobotSentence {
  type: SentenceTypeEnum;
  /**
   * Caso seja sentença do tipo DEFAULT | REGEX, use esta propriedade para definir o texto a ser procurado.
   */
  text?: string;
  /**
   * Caso seja sentença do tipo DEFAULT | REGEX, use esta propriedade para definir a posição do texto.
   */
  polygon?: Polygon;
  /**
   *  Caso seja sentença do tipo DEFAULT | REGEX, define a margem do polígono selecionado.
   */
  identificationThreshold?: number;
  /**
   * Caso seja sentença do tipo REGEX, define o padrão de REGEX.
   */
  pattern?: string;
  /**
   * Caso sentença do tipo XPATH, usar essa propriedade
   */
  xPath?: string;
  /**
   * Se a sentença não deve ser encontrada no documento
   * @default false
   */
  isDenied: boolean;
}

/**
 * Representa o DTO de uma configuração de uma extração de palavras ou tabelas
 */
export interface IRobotWordClick {
  /**
   * Por enquando usado internamente, ainda não existe uma propriedade que identifique o número da página.
   */
  _pageNumber?: number;
  polygon?: Polygon;
  word: IRobotWordSuggestion;
  extractionThreshold?: number;
  landmarks?: ILandmark[];
  tableConfiguration?: IRobotWordTableConfiguration;
  xPath?: string;
}

export interface IRobotWordSuggestion {
  originalText?: string;
  dataType: WordDataTypeEnum;
  word: TPrimitiveType;
  key: string;
}

export interface IRobotWordTableConfiguration {
  keyColumn: IRobotWordTableColumn;
  columns: IRobotWordTableColumn[];
}

export interface IRobotWordTableColumn {
  columnIndex: number;
  key: string;
  dataType: WordDataTypeEnum;
  verticalAlign?: IRobotWordTableKeyVerticalAlignEnum;
}

export interface IRobotExtractionResult {
  id: string;
  fileId: string;
  createDate: Date;
  createUserId: string;
  robots: IRobotExtracted[];
}

export interface IRobotExtracted {
  robot: IClassificationParameters;
  documents: IRobotDocumentExtracted[];
}

export interface IRobotDocumentExtracted {
  id: string;
  state: string;
  data: TRobotFormData;
  changedData: TRobotFormData;
  pages: number[];
}

/**
 * Resultado do preview de um robô para um arquivo.
 * @params isFound - Indica se todos as sentenças foram encontradas no arquivo.
 */
export interface IRobotPreview {
  fileId: string;
  pages: IRobotPreviewPage[];
}

/**
 * Resultado do preview de um robô para uma página de um arquivo.
 * @params isFound - Indica se todos as sentenças foram encontradas na página.
 */
export interface IRobotPreviewPage {
  sentences: IRobotPreviewSentence[];
  wordClicks: IRobotPreviewWordClick[];
  page: number;
  allSentencesTestsPassed: boolean;
  allWordClicksTestsPassed: boolean;
  isWordClickWarning: boolean;
}

export interface IRobotPreviewConfiguration {
  fileId: string;
  sentencesConfiguration: IRobotPreviewSentenceConfiguration[];
  wordclicksConfiguration: IRobotPreviewWordClickConfiguration[];
  disableWordClicksConfiguration: boolean;
}

export interface IRobotPreviewSentenceConfiguration {
  sentence: IRobotSentence;
  isExpectedToBeFound: boolean;
}

export interface IRobotPreviewWordClickConfiguration {
  key: string;
  valueExpected: TPrimitiveType;
  isRequired?: boolean;
}

export interface IRobotPreviewSentence {
  sentence: IRobotSentence;
  isPassed: boolean;
  isFound: boolean;
  isExpectedToBeFound: boolean;
  isVariableWarning: boolean;
}

export interface IRobotPreviewWordClick {
  adjustedThresholdArea: IAdjustedThresholdArea;
  adjustedPolygon: IAdjustedPolygon;
  distanceFromOriginalCentroid: number;
  isPassed: boolean;
  isWarning: boolean;
  key: string;
  valueActual: TPrimitiveType;
  valueExpected: TPrimitiveType;
  landmarkWordsPositionFound?: ILandmarkFound[];
}

export interface IAdjustedPolygon {
  centroid: Point2D;
  coordinates: Point2D[];
}

export interface IAdjustedThresholdArea {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

export enum IRobotWordTableKeyVerticalAlignEnum {
  top = 1,
  bottom = 2,
}

export type TRobotConstantFields = { [key: string]: string };

export type TRobotFormData = { [key: string]: any };

export type TRobotParseValue = { value: any; isParsed: boolean };

export type TDocumentSentencesAndWordClicks = {
  sentences: RobotSentence[];
  wordClicks: RobotWordClick[];
};
